// Rich per-contact sample data backing the /needs-review demo. Everything is
// keyed by the contact's lower-cased email. demoApi.js serves these through the
// network guard so the AI Conversation, Searches, Activity modals, the lead
// score popover, and the AI Notes popover all show realistic data for the
// seeded review drafts — without any backend call.
//
// The AI agent persona is shared across all sample contacts.
export const DEMO_AGENT = { name: "Olivia Bennett", email: "olivia@exampleagency.com" };

const now = Date.now();
const isoMinsAgo = (m) => new Date(now - m * 60000).toISOString();
const isoHoursAgo = (h) => new Date(now - h * 3600000).toISOString();
const isoDaysAgo = (d) => new Date(now - d * 86400000).toISOString();
const epochDaysAgo = (d) => Math.floor((now - d * 86400000) / 1000);

// ---------------------------------------------------------------------------
// Lead score breakdowns (consumed by <LeadScoreBadge components={...} />)
// ---------------------------------------------------------------------------
export const DEMO_LEAD_COMPONENTS = {
  "sarah.mitchell@example.com": {
    is_unsubbed: false,
    raw_sum: 168,
    agency_pct: 0.88,
    events: {
      property_analysed: { weight: 25, count: 3, decayed_sum: 64, last_ts: epochDaysAgo(0) },
      view_appraisal:    { weight: 20, count: 2, decayed_sum: 36, last_ts: epochDaysAgo(1) },
      sms_reply:         { weight: 30, count: 1, decayed_sum: 30, last_ts: epochDaysAgo(2) },
      email_click_au:    { weight: 10, count: 4, decayed_sum: 24, last_ts: epochDaysAgo(0) },
      email_open:        { weight: 5,  count: 11, decayed_sum: 14, last_ts: epochDaysAgo(0) },
    },
  },
  "james.nguyen@example.com": {
    is_unsubbed: false,
    raw_sum: 96,
    agency_pct: 0.74,
    events: {
      property_search:   { weight: 8,  count: 6, decayed_sum: 30, last_ts: epochDaysAgo(1) },
      property_viewed:   { weight: 6,  count: 5, decayed_sum: 22, last_ts: epochDaysAgo(2) },
      email_open:        { weight: 5,  count: 8, decayed_sum: 16, last_ts: epochDaysAgo(1) },
      email_click_au:    { weight: 10, count: 2, decayed_sum: 12, last_ts: epochDaysAgo(3) },
    },
  },
  "priya.sharma@example.com": {
    is_unsubbed: false,
    raw_sum: 71,
    agency_pct: 0.69,
    events: {
      property_analysed: { weight: 25, count: 1, decayed_sum: 18, last_ts: epochDaysAgo(6) },
      property_search:   { weight: 8,  count: 4, decayed_sum: 20, last_ts: epochDaysAgo(5) },
      email_open:        { weight: 5,  count: 6, decayed_sum: 12, last_ts: epochDaysAgo(4) },
      address_searched:  { weight: 7,  count: 2, decayed_sum: 9,  last_ts: epochDaysAgo(7) },
    },
  },
  "tom.andersson@example.com": {
    is_unsubbed: false,
    raw_sum: 42,
    agency_pct: 0.52,
    events: {
      property_analysed: { weight: 25, count: 1, decayed_sum: 19, last_ts: epochDaysAgo(9) },
      email_click_au:    { weight: 10, count: 2, decayed_sum: 14, last_ts: epochDaysAgo(11) },
      email_open:        { weight: 5,  count: 4, decayed_sum: 9,  last_ts: epochDaysAgo(9) },
    },
  },
  "olivia.brown@example.com": {
    is_unsubbed: false,
    raw_sum: 21,
    agency_pct: 0.31,
    events: {
      email_open:        { weight: 5,  count: 5, decayed_sum: 12, last_ts: epochDaysAgo(18) },
      email_click_au:    { weight: 10, count: 1, decayed_sum: 6,  last_ts: epochDaysAgo(22) },
      property_search:   { weight: 8,  count: 1, decayed_sum: 3,  last_ts: epochDaysAgo(45) },
    },
  },
  "daniel.lee@example.com": {
    is_unsubbed: false,
    raw_sum: 11,
    agency_pct: 0.18,
    events: {
      property_viewed:   { weight: 6,  count: 1, decayed_sum: 4,  last_ts: epochDaysAgo(34) },
      email_open:        { weight: 5,  count: 3, decayed_sum: 5,  last_ts: epochDaysAgo(36) },
      signed_up:         { weight: 6,  count: 1, decayed_sum: 2,  last_ts: epochDaysAgo(120) },
    },
  },
};

// ---------------------------------------------------------------------------
// AI Conversation threads (consumed by ThreadView via /ai-conversations/thread)
// ---------------------------------------------------------------------------
const tag = (slug, label, color, source = "ai") => ({ slug, label, color, category: "status", source });

export const DEMO_THREADS = {
  "sarah.mitchell@example.com": {
    ai_owns_relationship: true,
    contact: {
      first_name: "Sarah", last_name: "Mitchell",
      email: "sarah.mitchell@example.com", clean_mobile: "0412 345 678",
      suburb: "Balmain", postcode: "2041",
      handover_notes_count: 2,
      tags: [tag("hot-lead", "Hot lead", "#ef4444"), tag("seller", "Seller", "#0d9488", "human")],
      channels: { email: { available: true, reachable: true } },
    },
    pending_drafts: [],
    messages: [
      {
        direction: "outbound", channel: "email",
        from_email: DEMO_AGENT.email, to_email: "sarah.mitchell@example.com",
        subject: "Your property appraisal for 14 Darling Street",
        message_text:
          "Hi Sarah,\n\nThanks for requesting an appraisal on 14 Darling Street. Based on recent sales of comparable terraces in Balmain, I've estimated your home in the $1.75m–$1.95m range. I've attached the full report — happy to walk you through it whenever suits.\n\nWarm regards,\nOlivia",
        created_at: isoDaysAgo(6),
      },
      {
        direction: "inbound", channel: "email",
        from_email: "sarah.mitchell@example.com", to_email: DEMO_AGENT.email,
        subject: "Re: Your property appraisal for 14 Darling Street",
        message_text: "Thanks Olivia, this is really helpful. We'll have a read through and come back to you.",
        created_at: isoDaysAgo(5),
      },
      {
        direction: "outbound", channel: "sms",
        from_email: DEMO_AGENT.email, to_email: "sarah.mitchell@example.com",
        message_text: "Hi Sarah, just checking you got a chance to look over the Darling St appraisal? Happy to answer any questions — Olivia",
        created_at: isoDaysAgo(2),
      },
      {
        direction: "inbound", channel: "email",
        from_email: "sarah.mitchell@example.com", to_email: DEMO_AGENT.email,
        subject: "Re: Your property appraisal for 14 Darling Street",
        message_text:
          "Hi, thanks for the appraisal on Darling Street. We're thinking of listing in the next month or two — could you give me a call to talk through timing and your commission?",
        created_at: isoMinsAgo(8),
      },
    ],
  },
  "james.nguyen@example.com": {
    ai_owns_relationship: true,
    contact: {
      first_name: "James", last_name: "Nguyen",
      email: "james.nguyen@example.com", clean_mobile: "0423 887 210",
      suburb: "Hawthorn", postcode: "3122",
      handover_notes_count: 1,
      tags: [tag("nurturing", "Nurturing", "#f59e0b")],
      channels: { email: { available: true, reachable: true } },
    },
    pending_drafts: [],
    messages: [
      {
        direction: "outbound", channel: "email",
        from_email: DEMO_AGENT.email, to_email: "james.nguyen@example.com",
        subject: "New townhouses in Hawthorn this week",
        message_text:
          "Hi James,\n\nA couple of townhouses around Glenferrie Road have just come to market that look close to what you've been viewing. Want me to send the price guides and arrange inspections?\n\nCheers,\nOlivia",
        created_at: isoDaysAgo(8),
      },
      {
        direction: "inbound", channel: "email",
        from_email: "james.nguyen@example.com", to_email: DEMO_AGENT.email,
        subject: "Re: New townhouses in Hawthorn this week",
        message_text: "Yes please — particularly anything with 3 beds and a garage. We're pre-approved up to $1.3m.",
        created_at: isoDaysAgo(7),
      },
      {
        direction: "outbound", channel: "email",
        from_email: DEMO_AGENT.email, to_email: "james.nguyen@example.com",
        subject: "Re: New townhouses in Hawthorn this week",
        message_text: "Perfect, leave it with me. I'll line up a shortlist and send inspection times for the weekend.",
        created_at: isoDaysAgo(7),
      },
    ],
  },
  "priya.sharma@example.com": {
    ai_owns_relationship: true,
    contact: {
      first_name: "Priya", last_name: "Sharma",
      email: "priya.sharma@example.com", clean_mobile: "0438 552 901",
      suburb: "Toowong", postcode: "4066",
      handover_notes_count: 1,
      tags: [tag("buyer", "Buyer", "#3b82f6", "human")],
      channels: { email: { available: true, reachable: true } },
    },
    pending_drafts: [],
    messages: [
      {
        direction: "outbound", channel: "email",
        from_email: DEMO_AGENT.email, to_email: "priya.sharma@example.com",
        subject: "Your Toowong property search",
        message_text:
          "Hi Priya,\n\nThanks for searching units in Toowong. I've saved your criteria and will let you know the moment something matching comes up.\n\nOlivia",
        created_at: isoDaysAgo(34),
      },
      {
        direction: "inbound", channel: "email",
        from_email: "priya.sharma@example.com", to_email: DEMO_AGENT.email,
        subject: "Re: Your Toowong property search",
        message_text: "Thank you! No rush, we're hoping to buy before the end of the year.",
        created_at: isoDaysAgo(33),
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Activity timelines (consumed by ContactActivityModal via /activity-explorer)
//   email[]     -> rendered as email events
//   platform[]  -> rendered as platform events
// ---------------------------------------------------------------------------
function emailEvt(event_type, subject, tag, daysAgo, extra = {}) {
  return { event_type, email: extra.email, subject, tag, created_at: isoDaysAgo(daysAgo), ...extra };
}
function platEvt(event_type, detail, hoursAgo, extra = {}) {
  return { event_type, detail, created_at: isoHoursAgo(hoursAgo), ...extra };
}

export const DEMO_ACTIVITY = {
  "sarah.mitchell@example.com": {
    email: [
      emailEvt("Open", "Your property appraisal for 14 Darling Street", "appraisal_send", 6, { email: "sarah.mitchell@example.com" }),
      emailEvt("Click", "Your property appraisal for 14 Darling Street", "appraisal_send", 6, { email: "sarah.mitchell@example.com", clicked_url: "https://insights.exampleagency.com/appraisal/14-darling-st", country: "Australia" }),
      emailEvt("Open", "Balmain market update — autumn 2026", "market_update", 4, { email: "sarah.mitchell@example.com" }),
      emailEvt("Open", "Re: Your property appraisal for 14 Darling Street", "appraisal_send", 2, { email: "sarah.mitchell@example.com" }),
      emailEvt("Delivery", "Re: Your property appraisal for 14 Darling Street", "appraisal_send", 2, { email: "sarah.mitchell@example.com" }),
    ],
    platform: [
      platEvt("property_analysed", "14 Darling Street, Balmain NSW 2041", 0.3, { page_url: "https://insights.exampleagency.com/property/14-darling-st-balmain" }),
      platEvt("view_appraisal", "Viewed full appraisal — est. $1.85m", 1, {}),
      platEvt("send_property_report", "Property report sent to sarah.mitchell@example.com", 6 * 24, {}),
      platEvt("sms_sent", "Follow-up SMS delivered", 2 * 24, {}),
      platEvt("get_article", "Read: '5 things that add value before you sell'", 26, {}),
      platEvt("click_track_property", "Added 14 Darling Street to tracked properties", 30, {}),
    ],
  },
  "james.nguyen@example.com": {
    email: [
      emailEvt("Open", "New townhouses in Hawthorn this week", "property_update", 8, { email: "james.nguyen@example.com" }),
      emailEvt("Click", "New townhouses in Hawthorn this week", "property_update", 8, { email: "james.nguyen@example.com", clicked_url: "https://insights.exampleagency.com/property/8-glenferrie-rd", country: "Australia" }),
      emailEvt("Open", "Hawthorn market update", "market_update", 3, { email: "james.nguyen@example.com" }),
    ],
    platform: [
      platEvt("property_search", "Searched: Hawthorn VIC, 3+ beds, under $1.3m", 28, {}),
      platEvt("property_analysed", "8 Glenferrie Road, Hawthorn VIC 3122", 30, { page_url: "https://insights.exampleagency.com/property/8-glenferrie-rd-hawthorn" }),
      platEvt("click_track_property", "Tracked 8 Glenferrie Road", 48, {}),
      platEvt("get_article", "Read: 'First-home buyer guide to Melbourne's inner east'", 72, {}),
    ],
  },
  "priya.sharma@example.com": {
    email: [
      emailEvt("Open", "Your Toowong property search", "onboarding_welcome", 34, { email: "priya.sharma@example.com" }),
      emailEvt("Open", "Toowong market update", "market_update", 5, { email: "priya.sharma@example.com" }),
    ],
    platform: [
      platEvt("property_search", "Searched: Toowong QLD, 2 beds, under $900k", 120, {}),
      platEvt("property_analysed", "22 Sherwood Road, Toowong QLD 4066", 144, { page_url: "https://insights.exampleagency.com/property/22-sherwood-rd-toowong" }),
      platEvt("save_notification_settings", "Saved weekly property alerts", 150, {}),
    ],
  },
};

// ---------------------------------------------------------------------------
// AI handover notes (consumed by /agent-list-contact-handover-notes)
// ---------------------------------------------------------------------------
function note(id, created_by, daysAgo, text) {
  return { id, created_by, created_at: isoDaysAgo(daysAgo), note: text };
}

export const DEMO_NOTES = {
  "sarah.mitchell@example.com": [
    note("dn-s1", "AI Agent", 6, "Requested an appraisal for 14 Darling Street. Owner-occupier, renovated terrace. Sent full report and flagged as a likely seller."),
    note("dn-s2", "AI Agent", 0, "Replied asking to discuss listing timing and commission — strong intent to sell within 1–2 months. Recommend a callback today; drafted a reply offering a call."),
  ],
  "james.nguyen@example.com": [
    note("dn-j1", "AI Agent", 7, "Active buyer, pre-approved to $1.3m. Wants 3-bed townhouses with a garage around Hawthorn/Glenferrie Rd. Nurturing with new-listing alerts."),
  ],
  "priya.sharma@example.com": [
    note("dn-p1", "AI Agent", 33, "Early-stage buyer in Toowong, budget ~$900k, timeline end of year. Set up weekly alerts; low urgency, keep warm."),
  ],
};

// ---------------------------------------------------------------------------
// Property searches per contact (consumed by ContactSearchesModal, which
// embeds PropertySearches and filters /property-report-requests by email).
// Returned flattened by demoApi so the modal's client-side email filter works.
// ---------------------------------------------------------------------------
const img = (id) => `https://images.unsplash.com/${id}?w=400&q=60&auto=format`;

function search(property_id, email, name, daysAgo, req, meta = {}) {
  return {
    property_id, email,
    contact_full_name: name, contact_email: email,
    agent_name: DEMO_AGENT.name,
    created_at: isoDaysAgo(daysAgo),
    metadata: { property_id, ...meta, request: req },
  };
}

export const DEMO_CONTACT_SEARCHES = [
  search("s-sarah-1", "sarah.mitchell@example.com", "Sarah Mitchell", 0,
    { address_text: "14 Darling Street, Balmain NSW 2041", report_type: "property_report", property_type: "House", bedrooms: 4, bathrooms: 2, carspaces: 1, land_area: 412, suburb_id: "Balmain", media_urls: [img("photo-1568605114967-8130f3a36994")] },
    { valuation: 1850000, valuation_lower: 1750000, valuation_upper: 1950000, rental_estimate: 980, rental_estimate_lower: 920, rental_estimate_upper: 1040 }),
  search("s-sarah-2", "sarah.mitchell@example.com", "Sarah Mitchell", 27,
    { address_text: "9 Mort Street, Balmain NSW 2041", report_type: "property_analysis", property_type: "House", bedrooms: 3, bathrooms: 1, carspaces: 0, land_area: 233, suburb_id: "Balmain", media_urls: [img("photo-1572120360610-d971b9d7767c")] },
    { valuation: 1620000, valuation_lower: 1550000, valuation_upper: 1700000 }),
  search("s-james-1", "james.nguyen@example.com", "James Nguyen", 2,
    { address_text: "8 Glenferrie Road, Hawthorn VIC 3122", report_type: "property_analysis", property_type: "Townhouse", bedrooms: 3, bathrooms: 2, carspaces: 2, land_area: 260, suburb_id: "Hawthorn", media_urls: [img("photo-1570129477492-45c003edd2be")] },
    { valuation: 1240000, valuation_lower: 1180000, valuation_upper: 1300000, rental_estimate: 720 }),
  search("s-james-2", "james.nguyen@example.com", "James Nguyen", 9,
    { address_text: "31 Power Street, Hawthorn VIC 3122", report_type: "property_report", property_type: "Townhouse", bedrooms: 3, bathrooms: 2, carspaces: 1, land_area: 210, suburb_id: "Hawthorn", media_urls: [img("photo-1576941089067-2de3c901e126")] },
    { valuation: 1175000, valuation_lower: 1120000, valuation_upper: 1230000 }),
  search("s-priya-1", "priya.sharma@example.com", "Priya Sharma", 6,
    { address_text: "22 Sherwood Road, Toowong QLD 4066", report_type: "property_report", property_type: "Unit", bedrooms: 2, bathrooms: 1, carspaces: 1, suburb_id: "Toowong", media_urls: [img("photo-1512917774080-9991f1c4c750")] },
    { valuation: 890000, valuation_lower: 850000, valuation_upper: 930000, rental_estimate: 560 }),
];
